import dayjs, { Dayjs } from 'dayjs';
import { size } from 'lodash';
import { deassembleRange, normalizeDefaultRange } from '../helpers/dateTimes';

export interface DateTimePickerResponse {
  result: GetServiceOptionsResponse;
}

export interface GetServiceOptionsResponse {
  currency: string;
  /**
   * The date ranges step in minutes
   *
   * ex: 2021-06-25T11:00:00Z/2021-06-25T15:45:00Z with a step value of 15
   * it's meaning that the date ranges in prices field is from 11:00 to 15:45 and has to divides in
   * peaces of 15 minutes each like: 11:00 - 11:15 - 11:30 - 11:45 ....
   */
  step: number;
  prices: PricesByRange;
  originalPrices: PricesByRange;
  getServiceOptionToken: string;
  success: boolean;
  defaultRange: string;
  defaultRanges?: string[] | null;
}

export interface PricesByRange {
  /**
   * The object key is composed by 2 strings separated by a slash first string before
   * the slash is the date where the range start, while the second after the slash,
   * is when the range end
   *
   * ex: 2021-06-25T11:00:00Z/2021-06-25T15:45:00Z the range start at 2021-06-25T11:00:00Z
   * and end at 2021-06-25T15:45:00Z
   */
  [key: `${string}/${string}`]: number;
}

export type LinkedTimes = {
  /**
   * The value is the linked time price
   */
  [linkedTime: string]: number;
};

export type RangesByTime = {
  [time: string]: LinkedTimes;
};

export type TimeRangesPerDate = {
  [date: string]: RangesByTime;
};

export type DefaultRange = {
  date: string;
  from: string;
  to: string;
};

export class DateTimePickerModel {
  readonly currency: string;
  readonly startTimeRanges: TimeRangesPerDate = {};
  readonly endTimeRanges: TimeRangesPerDate = {};
  readonly startOriginalTimeRanges: TimeRangesPerDate = {};
  readonly endOriginalTimeRanges: TimeRangesPerDate = {};
  readonly defaultRanges: DefaultRange[];
  readonly availableDates: Dayjs[] | null;

  constructor(
    private readonly dateTimePickerResponse: GetServiceOptionsResponse,
    isOnlyFreeAppointment: boolean
  ) {
    this.currency = dateTimePickerResponse.currency;
    this.normalizeDatesAndTimes(isOnlyFreeAppointment);
    this.availableDates = this.getDates();
    this.defaultRanges = this.getDefaultRangesNormalized();
  }

  private readonly getDates = (): Dayjs[] | null => {
    if (this.startTimeRanges) {
      return Object.keys(this.startTimeRanges).map((s) => {
        return dayjs(s);
      });
    } else {
      return null;
    }
  };

  private readonly normalizeDatesAndTimes = (
    isOnlyFreeAppointment: boolean
  ): void => {
    let rangePrices: PricesByRange = this.dateTimePickerResponse.prices;
    //TODO: aggiungi la proprietà this.dateTimePickerResponse.originalPrices. Se sono diversi c'è uno sconto applicato, mostrare il prezzo sbarrato + prezzo nuovo
    let rangeOriginalPrices: PricesByRange =
      this.dateTimePickerResponse.originalPrices;
    if (isOnlyFreeAppointment) {
      rangePrices = Object.keys(this.dateTimePickerResponse.prices)
        .filter((k) => {
          return (
            this.dateTimePickerResponse.prices[k as `${string}/${string}`] === 0
          );
        })
        .reduce((prev, current) => {
          return {
            ...prev,
            [current]: 0,
          };
        }, {});

      rangeOriginalPrices = Object.keys(
        this.dateTimePickerResponse.originalPrices
      )
        .filter((k) => {
          return (
            this.dateTimePickerResponse.prices[k as `${string}/${string}`] === 0
          );
        })
        .reduce((prev, current) => {
          return {
            ...prev,
            [current]: 0,
          };
        }, {});
    }

    Object.keys(rangePrices).forEach((range, i) => {
      const { fromTime, endTime, date, price } = deassembleRange(
        range,
        rangePrices
      );
      const {
        fromTime: originalFormTime,
        endTime: originalEndTime,
        date: originalDate,
        price: originalPrice,
      } = deassembleRange(
        Object.keys(rangeOriginalPrices)[i],
        rangeOriginalPrices
      );

      this.populateStartTimeRanges(date, fromTime, endTime, price!);
      this.populateEndTimeRanges(date, endTime, fromTime, price!);
      this.populateStartOriginalTimeRanges(
        originalDate,
        originalFormTime,
        originalEndTime,
        originalPrice!
      );
      this.populateEndOriginalTimeRanges(
        originalDate,
        originalEndTime,
        originalFormTime,
        originalPrice!
      );
    });
  };

  private readonly setHoursMinuteOnCurrentDay = (
    hours: number,
    minutes: number
  ) => {
    return dayjs().set('hour', hours).set('minute', minutes);
  };

  private readonly normalizeIncorrectDefaultRange = (
    currentDateRanges: RangesByTime
  ) => {
    let max = 0;
    let from = '';
    let to = '';
    Object.keys(currentDateRanges)
      .sort()
      .forEach((keyHour) => {
        const startTimeSplit = keyHour.split(':');
        const startTime = this.setHoursMinuteOnCurrentDay(
          parseInt(startTimeSplit[0]),
          parseInt(startTimeSplit[1])
        );

        const endIndexTimeRange = size(currentDateRanges[keyHour]) - 1;
        const endTimeSplit = Object.keys(currentDateRanges[keyHour])
          .sort()
          [endIndexTimeRange].split(':');
        const endTime = this.setHoursMinuteOnCurrentDay(
          parseInt(endTimeSplit[0]),
          parseInt(endTimeSplit[1])
        );

        const diff = endTime.diff(startTime, 'minute');
        if (
          diff > max &&
          parseInt(endTimeSplit[1]) % 30 === 0 &&
          parseInt(startTimeSplit[1]) % 30 === 0
        ) {
          max = diff;
          from = keyHour;
          to = Object.keys(currentDateRanges[keyHour]).sort()[
            endIndexTimeRange
          ];
        }
      });

    return { from, to };
  };

  private readonly getDefaultRangesNormalized = (): DefaultRange[] => {
    const normalizedRanges =
      this.dateTimePickerResponse.defaultRanges?.map<DefaultRange>((dr) => {
        let normalizedDefaultRange = normalizeDefaultRange(dr);
        const fromTimeSplit = normalizedDefaultRange.from.split(':');
        const toTimeSplit = normalizedDefaultRange.to.split(':');
        if (
          parseInt(fromTimeSplit[1]) % 30 !== 0 ||
          parseInt(toTimeSplit[1]) % 30 !== 0
        ) {
          const currentDateRanges =
            this.startTimeRanges[normalizedDefaultRange.date];
          const { from, to } =
            this.normalizeIncorrectDefaultRange(currentDateRanges);
          normalizedDefaultRange = {
            date: normalizedDefaultRange.date,
            from: from,
            to: to,
          };
        }
        return normalizedDefaultRange;
      });
    return normalizedRanges ?? [];
  };

  private populateEndTimeRanges(
    date: string,
    endTime: string,
    fromTime: string,
    price: number
  ) {
    if (!this.endTimeRanges[date]) {
      this.endTimeRanges[date] = {};
    }

    if (!this.endTimeRanges[date][endTime]) {
      this.endTimeRanges[date][endTime] = {};
    }

    this.endTimeRanges[date][endTime][fromTime] = price;
  }

  private populateEndOriginalTimeRanges(
    date: string,
    endTime: string,
    fromTime: string,
    price: number
  ) {
    if (!this.endOriginalTimeRanges[date]) {
      this.endOriginalTimeRanges[date] = {};
    }

    if (!this.endOriginalTimeRanges[date][endTime]) {
      this.endOriginalTimeRanges[date][endTime] = {};
    }

    this.endOriginalTimeRanges[date][endTime][fromTime] = price;
  }

  private populateStartTimeRanges(
    date: string,
    fromTime: string,
    endTime: string,
    price: number
  ) {
    if (!this.startTimeRanges[date]) {
      this.startTimeRanges[date] = {};
    }

    if (!this.startTimeRanges[date][fromTime]) {
      this.startTimeRanges[date][fromTime] = {};
    }

    this.startTimeRanges[date][fromTime][endTime] = price;
  }

  private populateStartOriginalTimeRanges(
    date: string,
    fromTime: string,
    endTime: string,
    price: number
  ) {
    if (!this.startOriginalTimeRanges[date]) {
      this.startOriginalTimeRanges[date] = {};
    }

    if (!this.startOriginalTimeRanges[date][fromTime]) {
      this.startOriginalTimeRanges[date][fromTime] = {};
    }
    this.startOriginalTimeRanges[date][fromTime][endTime] = price;
  }
}
